import React, { useState, useEffect } from "react";
import { UARUserSelector } from "./UARUserSelector";
import { EntraUser } from "../../../../../services/profileService/model/entraUser/EntraUser";
import { HSTSelectRole } from "../../../components/hstRoles/HSTSelectRole";
import { Typography, Box, Paper, Grid, Divider, FormHelperText } from "@mui/material";
import { FacilitySelector } from "../../../components/facilitySelector/FacilitySelector";

const HSTTicketModule: React.FC<{ updateUARFields: (fields: { selectedUser: EntraUser; selectedRole: string; locations: string[] }) => void }> = ({ updateUARFields }) => {
  const [selectedUser, setSelectedUser] = useState<EntraUser | null>(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  
  // Validation states
  const [userTouched, setUserTouched] = useState(false);
  const [roleTouched, setRoleTouched] = useState(false);
  const [locationsTouched, setLocationsTouched] = useState(false);
  
  // Error states
  const userError = userTouched && !selectedUser;
  const roleError = roleTouched && !selectedRole;
  const locationsError = locationsTouched && (!selectedLocations || selectedLocations.length === 0);
  
  const handleUserChange = (user: EntraUser) => {
    setSelectedUser(user);
    setUserTouched(true);
    updateUARFields({ 
      selectedUser: user, 
      selectedRole: selectedRole,
      locations: selectedLocations
    });
  };

  const handleRoleChange = (role: string) => {
    setSelectedRole(role);
    setRoleTouched(true);
    updateUARFields({ 
      selectedUser: selectedUser || new EntraUser(), 
      selectedRole: role,
      locations: selectedLocations
    });
  };

  const handleLocationsChange = (locations: string[]) => {
    setSelectedLocations(locations);
    setLocationsTouched(true);
    updateUARFields({ 
      selectedUser: selectedUser || new EntraUser(), 
      selectedRole: selectedRole,
      locations: locations
    });
  };

  // Mark all fields as touched when form is submitted
  useEffect(() => {
    const markFieldsAsTouched = () => {
      setUserTouched(true);
      setRoleTouched(true);
      setLocationsTouched(true);
    };

    // Find the closest form element and add submit listener
    const form = document.querySelector('form');
    if (form) {
      form.addEventListener('submit', markFieldsAsTouched);
      return () => form.removeEventListener('submit', markFieldsAsTouched);
    }
  }, []);

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2, backgroundColor: '#fcfcfc' }}>
      <Typography variant="h6" sx={{ mb: 3, color: '#2c3e50', fontWeight: 600 }}>
        HST Access Request Details
      </Typography>
      <Divider sx={{ mb: 3 }} />
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 500, color: '#34495e' }}>
              User <span style={{ color: 'red' }}>*</span>
            </Typography>
            <UARUserSelector
              value={selectedUser?.displayName || ""}
              updateFunc={(value: EntraUser) => {
                const user = new EntraUser();
                user.displayName = value.displayName;
                user.id = value.id;
                user.mail = value.mail;
                user.jobTitle = value.jobTitle;
                user.department = value.department;
                user.mail = value.mail;
                user.companyName = value.companyName;
                user.userPrincipalName = value.userPrincipalName;
                user.managerId = value.managerId;
                handleUserChange(user);
              }}
            />
            {userError && (
              <FormHelperText error>User is required</FormHelperText>
            )}
          </Box>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 500, color: '#34495e' }}>
              Role <span style={{ color: 'red' }}>*</span>
            </Typography>
            <HSTSelectRole
              selectedRole={selectedRole}
              onChange={(role) => handleRoleChange(role)}
            />
            {roleError && (
              <FormHelperText error>Role is required</FormHelperText>
            )}
          </Box>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 500, color: '#34495e' }}>
              Facility <span style={{ color: 'red' }}>*</span>
            </Typography>
            <FacilitySelector
              selectedLocations={selectedLocations}
              onChange={handleLocationsChange}
              ticketType="HST"
            />
            {locationsError && (
              <FormHelperText error>Facility is required</FormHelperText>
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export { HSTTicketModule };
